// The language everything falls back to: a null/'en' reading preference, and any
// resource without a served translation. Source content is authored in English.
export const ENGLISH_LANGUAGE_CODE = 'en';

// Single source of truth for the languages Duke can machine-translate into. The
// backend producer's `TARGET_LANGUAGES` derives from here, so the two can't drift.
// `englishName` is shown to authors, `endonym` to learners in their own script,
// `rtl` flags right-to-left scripts for the `dir` attribute.
export type CourseTranslationLanguage = {
  // BCP-47 code, matches the ContentTranslation.language column.
  code: string;
  englishName: string;
  endonym: string;
  rtl: boolean;
};

export const COURSE_TRANSLATION_LANGUAGES = [
  { code: 'vi', englishName: 'Vietnamese', endonym: 'Tiếng Việt', rtl: false },
  { code: 'zh-Hans', englishName: 'Chinese (Simplified)', endonym: '简体中文', rtl: false },
  { code: 'ar', englishName: 'Arabic', endonym: 'العربية', rtl: true },
  { code: 'yue', englishName: 'Cantonese', endonym: '廣東話', rtl: false },
  { code: 'pa', englishName: 'Punjabi', endonym: 'ਪੰਜਾਬੀ', rtl: false },
  { code: 'it', englishName: 'Italian', endonym: 'Italiano', rtl: false },
  { code: 'prs', englishName: 'Dari', endonym: 'دری', rtl: true },
] as const satisfies readonly CourseTranslationLanguage[];

export type CourseTranslationLanguageCode = (typeof COURSE_TRANSLATION_LANGUAGES)[number]['code'];

// In display order; the backend's `TARGET_LANGUAGES` re-exports this.
export const COURSE_TRANSLATION_LANGUAGE_CODES: readonly string[] =
  COURSE_TRANSLATION_LANGUAGES.map((language) => language.code);

export function getLanguage(code: string): CourseTranslationLanguage | undefined {
  return COURSE_TRANSLATION_LANGUAGES.find((language) => language.code === code);
}

export function isRtlLanguage(code: string): boolean {
  return getLanguage(code)?.rtl ?? false;
}

// Unknown codes (including English) fall back to left-to-right.
export function languageDir(code: string): 'ltr' | 'rtl' {
  return isRtlLanguage(code) ? 'rtl' : 'ltr';
}
